/**
 * masstimes.org church data.
 *
 * The masstimes.org site and apps read from one endpoint: churches nearest a
 * point, nearest first, one page at a time. Fetching is split from parsing so
 * the same parser serves a direct call and a call through the Pages Function
 * relay (`functions/api/masstimes.ts`), whichever CORS turns out to allow.
 *
 * Every string in the upstream JSON is padded or loosely typed ("Sunday    ",
 * "39.74…" as a string, "English "), so normalisation happens here, once, and
 * nothing downstream sees the raw shape.
 */

import { getJson, type RequestOptions } from "@viamissa/http";

/** The upstream endpoint. Takes `lat`, `long` and a 1-based `pg`. */
export const UPSTREAM = "https://apiv4.updateparishdata.org/Churchs/";

/** Where the site's relay lives, relative to the page. */
export const RELAY = "/api/masstimes";

export type ServiceKind =
  | "sunday" // Sunday Mass
  | "vigil" // Saturday evening Mass that fulfils the Sunday obligation
  | "weekday" // weekday Mass
  | "holyday" // Mass on a holy day of obligation
  | "holyday-vigil" // evening Mass before a holy day
  | "confession"
  | "adoration"
  | "devotion"
  | "other";

export type ServiceCategory = "mass" | "confession" | "adoration" | "devotion" | "other";

export type ClockTime = { hour: number; minute: number };

export type Service = {
  kind: ServiceKind;
  category: ServiceCategory;
  /** 0 = Sunday … 6 = Saturday; null when the listing names no weekday (e.g. holy days). */
  weekday: number | null;
  start: ClockTime | null;
  end: ClockTime | null;
  language: string | null;
  comment: string | null;
  /** Perpetual Adoration: the chapel is always open. */
  perpetual: boolean;
  /** The upstream label, kept for display ("Week Days", "Adorations"). */
  label: string;
};

export type Church = {
  id: string;
  name: string;
  type: string | null;
  lat: number;
  lng: number;
  street: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
  website: string | null;
  email: string | null;
  diocese: string | null;
  rite: string | null;
  language: string | null;
  pastor: string | null;
  directions: string | null;
  wheelchairAccess: boolean;
  /** As listed upstream, e.g. "9/11/2025 5:15:39 PM". */
  lastUpdated: string | null;
  services: Service[];
};

export type ChurchPage = {
  churches: Church[];
  /**
   * Farthest result on the page, in km from the query point, measured here
   * rather than trusting the upstream `distance` (which is in miles).
   */
  radiusKm: number;
  /** Number of raw rows, used to tell a full page from the last one. */
  size: number;
};

const str = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const num = (value: unknown): number | null => {
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export function parseWeekday(value: unknown): number | null {
  const text = str(value)?.toLowerCase();
  if (!text) return null;
  const index = WEEKDAYS.findIndex((day) => day === text || day.slice(0, 3) === text.slice(0, 3));
  return index === -1 ? null : index;
}

/** "08:30:00", "8:30", "8:30 PM", "20:30" → clock time. */
export function parseClock(value: unknown): ClockTime | null {
  const text = str(value);
  if (!text) return null;
  const match = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?$/);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridiem = match[3]?.toLowerCase();
  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

/** Saturday Masses from this hour on are anticipated Sunday Masses. */
export const VIGIL_FROM_HOUR = 14;

export function classify(label: string, weekday: number | null, start: ClockTime | null): ServiceKind {
  const type = label.toLowerCase();
  if (type.startsWith("confession")) return "confession";
  if (type.startsWith("adoration")) return "adoration";
  if (type.startsWith("devotion")) return "devotion";
  if (type.startsWith("holy day")) return "holyday";
  if (type === "vigil") return "holyday-vigil";
  if (type === "weekend") {
    if (weekday === 0) return "sunday";
    if (weekday === 6 && (start?.hour ?? 0) >= VIGIL_FROM_HOUR) return "vigil";
    return "weekday";
  }
  if (type.startsWith("week")) return "weekday";
  return "other";
}

export function categoryOf(kind: ServiceKind): ServiceCategory {
  switch (kind) {
    case "confession":
    case "adoration":
    case "devotion":
    case "other":
      return kind;
    default:
      return "mass";
  }
}

function parseService(raw: Record<string, unknown>): Service | null {
  const label = str(raw.service_typename) ?? "Other";
  const weekday = parseWeekday(raw.day_of_week);
  const start = parseClock(raw.time_start);
  const kind = classify(label, weekday, start);
  return {
    kind,
    category: categoryOf(kind),
    weekday,
    start,
    end: parseClock(raw.time_end),
    language: str(raw.language),
    comment: str(raw.comment),
    perpetual: raw.is_perpetual === true,
    label,
  };
}

/** Accept "www.x.org" as well as "http://x.org"; drop anything that is not a web address. */
export function normaliseWebsite(value: unknown): string | null {
  const text = str(value);
  if (!text) return null;
  const withScheme = /^https?:\/\//i.test(text) ? text : `http://${text}`;
  try {
    const url = new URL(withScheme);
    return url.hostname.includes(".") ? url.toString() : null;
  } catch {
    return null;
  }
}

export function parseChurch(raw: Record<string, unknown>): Church | null {
  const id = str(raw.id) ?? (typeof raw.id === "number" ? String(raw.id) : null);
  const lat = num(raw.latitude);
  const lng = num(raw.longitude);
  const name = str(raw.name);
  if (!id || lat === null || lng === null || !name) return null;

  const times = Array.isArray(raw.church_worship_times) ? raw.church_worship_times : [];
  const services = times
    .filter((entry): entry is Record<string, unknown> => typeof entry === "object" && entry !== null)
    .map(parseService)
    .filter((service): service is Service => service !== null);

  return {
    id,
    name,
    type: str(raw.church_type_name),
    lat,
    lng,
    street: str(raw.church_address_street_address),
    city: str(raw.church_address_city_name),
    region: str(raw.church_address_providence_name),
    postalCode: str(raw.church_address_postal_code),
    country: str(raw.church_address_country_territory_name),
    phone: str(raw.phone_number),
    website: normaliseWebsite(raw.url),
    email: str(raw.email),
    diocese: [str(raw.diocese_type_name), str(raw.diocese_name)].every(Boolean)
      ? `${str(raw.diocese_type_name)} of ${str(raw.diocese_name)}`
      : str(raw.diocese_name),
    rite: str(raw.rite_type_name),
    language: str(raw.language_name),
    pastor: str(raw.pastors_name),
    directions: str(raw.directions),
    wheelchairAccess: raw.wheel_chair_access === true,
    lastUpdated: str(raw.last_update),
    services,
  };
}

const EARTH_KM = 6371.0088;
const rad = (degrees: number) => (degrees * Math.PI) / 180;

function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const h =
    Math.sin(rad(bLat - aLat) / 2) ** 2 +
    Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(rad(bLng - aLng) / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Parse one page of results for a query at (`lat`, `lng`). */
export function parsePage(body: unknown, lat: number, lng: number): ChurchPage {
  const rows = Array.isArray(body) ? body : [];
  const churches = rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(parseChurch)
    .filter((church): church is Church => church !== null);
  const radiusKm = churches.reduce((max, church) => Math.max(max, distanceKm(lat, lng, church.lat, church.lng)), 0);
  return { churches, radiusKm, size: rows.length };
}

export type PageQuery = {
  lat: number;
  lng: number;
  /** 1-based. */
  page?: number;
  /** `RELAY` (default), `UPSTREAM`, or any endpoint taking the same parameters. */
  endpoint?: string;
  http?: RequestOptions;
};

export function pageUrl(query: PageQuery): string {
  const { lat, lng, page = 1, endpoint = RELAY } = query;
  const params = new URLSearchParams({ lat: lat.toFixed(5), long: lng.toFixed(5), pg: String(page) });
  return `${endpoint}?${params}`;
}

/** Fetch and parse one page of churches nearest a point. */
export async function fetchChurchPage(query: PageQuery): Promise<ChurchPage> {
  const body = await getJson<unknown>(pageUrl(query), query.http);
  return parsePage(body, query.lat, query.lng);
}

/** One-line postal address. */
export function addressOf(church: Church): string {
  const cityLine = [church.city, [church.region, church.postalCode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [church.street, cityLine].filter(Boolean).join(", ");
}

/** `tel:` link with only the dialable characters. */
export function telLink(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}
